import { useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ProjectCustomField } from "@/services/projectCustomFieldsApi";
import type { ProjectDocumentItem } from "@/services/projectsApi";
import type { Document } from "@/types/models";

// Selection state shape — empty Set (or missing key) means "no constraint".
export type FilterSelection = Record<string, Set<string>>;

interface FilterField {
  key: string;
  label: string;
  // All distinct values present on the active docs, sorted, with their counts.
  values: { value: string; count: number }[];
  // Pulls the doc's value(s) for this field. Returns [] when absent.
  getValues: (doc: Document, pdoc: ProjectDocumentItem) => string[];
}

// Standard metadata fields that act as facets, in display order. Each entry
// declares how to read its value(s) from a Document — single-valued fields
// return a one-element array when set, `tags` is already a list.
const STANDARD_FIELDS: Array<Omit<FilterField, "values">> = [
  { key: "std:sourceType", label: "Source Type", getValues: (d) => (d.sourceType ? [d.sourceType] : []) },
  { key: "std:evidenceType", label: "Study Type", getValues: (d) => (d.evidenceType ? [d.evidenceType] : []) },
  { key: "std:sourceOrganisation", label: "Source Organisation", getValues: (d) => (d.sourceOrganisation ? [d.sourceOrganisation] : []) },
  { key: "std:country", label: "Country", getValues: (d) => (d.country ? [d.country] : []) },
  { key: "std:dateRelevance", label: "Date Relevance", getValues: (d) => (d.dateRelevance ? [d.dateRelevance] : []) },
  { key: "std:year", label: "Year", getValues: (d) => (d.year ? [d.year] : []) },
  { key: "std:language", label: "Language", getValues: (d) => (d.language ? [d.language] : []) },
  { key: "std:ownership", label: "Ownership", getValues: (d) => (d.ownership ? [d.ownership] : []) },
  { key: "std:tags", label: "Tags", getValues: (d) => d.tags ?? [] },
];

function customFieldGetValues(field: ProjectCustomField) {
  // Per-document custom-field values are keyed by ProjectCustomField.id —
  // not by field.name. The upload form and the project-documents PATCH both
  // round-trip through this id-keyed shape, so the panel must read the same
  // way or every value comes back undefined.
  return (_doc: Document, pdoc: ProjectDocumentItem): string[] => {
    const raw = pdoc.customFieldValues?.[field.id];
    if (raw == null) return [];
    if (Array.isArray(raw)) return raw.filter((v): v is string => typeof v === "string" && v.length > 0);
    if (typeof raw === "string") return raw ? [raw] : [];
    if (typeof raw === "number" || typeof raw === "boolean") return [String(raw)];
    return [];
  };
}

// Custom fields included as filters. Selects are the natural fit; text /
// textarea are included too because in practice the LLM tends to extract
// short, repeated labels (e.g. "Oncology", "Phase III") that work fine as
// a checkbox list — and the empty-section guard hides any field that has
// no present values. Numeric and date are still excluded — they need a
// range/date-picker UX, not checkboxes.
export const FILTERABLE_CUSTOM_TYPES = new Set([
  "single_select",
  "multi_select",
  "text",
  "textarea",
]);

// Stable list of every possible filter-field key + label, computed against
// the schema (standard fields + project custom-field defs). Used by the
// admin config UI: the admin picks from this universe regardless of
// whether the current docs happen to populate each field. The chat panel
// can intersect this with a project's `visibleChatFilterKeys` to decide
// what to show.
export interface FilterFieldOption {
  key: string;
  label: string;
  group: "standard" | "custom";
}

export function listAllFilterFieldOptions(
  customFieldDefs: ProjectCustomField[],
): FilterFieldOption[] {
  const standard: FilterFieldOption[] = STANDARD_FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    group: "standard",
  }));
  const custom: FilterFieldOption[] = customFieldDefs
    .filter((f) => FILTERABLE_CUSTOM_TYPES.has(f.field_type))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((f) => ({ key: `cf:${f.id}`, label: f.name, group: "custom" }));
  return [...standard, ...custom];
}

interface BuildFilterFieldsArgs {
  activeProjectDocs: ProjectDocumentItem[];
  // Active project docs joined with their Document record (skip any that
  // can't be resolved — they wouldn't be searchable anyway).
  docsById: Map<string, Document>;
  customFieldDefs: ProjectCustomField[];
}

export function buildFilterFields({
  activeProjectDocs,
  docsById,
  customFieldDefs,
}: BuildFilterFieldsArgs): FilterField[] {
  const pairs: Array<{ doc: Document; pdoc: ProjectDocumentItem }> = [];
  for (const pdoc of activeProjectDocs) {
    const doc = docsById.get(pdoc.documentId);
    if (doc) pairs.push({ doc, pdoc });
  }

  const customDescriptors: Array<Omit<FilterField, "values">> = customFieldDefs
    .filter((f) => FILTERABLE_CUSTOM_TYPES.has(f.field_type))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((f) => ({
      key: `cf:${f.id}`,
      label: f.name,
      getValues: customFieldGetValues(f),
    }));

  const descriptors = [...STANDARD_FIELDS, ...customDescriptors];

  // Aggregate distinct values + per-value counts from what's actually present.
  // A field with zero present values is hidden — no point showing an empty
  // facet to the user.
  const result: FilterField[] = [];
  for (const d of descriptors) {
    const counts = new Map<string, number>();
    for (const { doc, pdoc } of pairs) {
      const values = d.getValues(doc, pdoc);
      const distinct = new Set(values);
      for (const v of distinct) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
    }
    if (counts.size === 0) continue;
    const values = Array.from(counts.entries())
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => a.value.localeCompare(b.value));
    result.push({ ...d, values });
  }
  return result;
}

// A document matches when, for every field with a non-empty selection, at
// least one of its values is in the selection. Empty/absent selections do
// not constrain.
export function applyFilters(
  filterFields: FilterField[],
  selection: FilterSelection,
  activeProjectDocs: ProjectDocumentItem[],
  docsById: Map<string, Document>,
): string[] {
  const activeFields = filterFields.filter((f) => {
    const sel = selection[f.key];
    return sel && sel.size > 0;
  });

  const matched: string[] = [];
  for (const pdoc of activeProjectDocs) {
    const doc = docsById.get(pdoc.documentId);
    if (!doc) continue;
    const ok = activeFields.every((f) => {
      const sel = selection[f.key]!;
      const docValues = f.getValues(doc, pdoc);
      return docValues.some((v) => sel.has(v));
    });
    if (ok) matched.push(pdoc.documentId);
  }
  return matched;
}

interface ProjectChatFiltersProps {
  filterFields: FilterField[];
  selection: FilterSelection;
  onSelectionChange: (next: FilterSelection) => void;
  totalCount: number;
  matchedCount: number;
}

const ProjectChatFilters = ({
  filterFields,
  selection,
  onSelectionChange,
  totalCount,
  matchedCount,
}: ProjectChatFiltersProps) => {
  const activeFilterCount = useMemo(
    () =>
      Object.values(selection).reduce((acc, set) => acc + (set?.size ?? 0), 0),
    [selection],
  );

  const toggleValue = (fieldKey: string, value: string) => {
    const current = selection[fieldKey] ?? new Set<string>();
    const next = new Set(current);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    const nextSelection = { ...selection };
    if (next.size === 0) delete nextSelection[fieldKey];
    else nextSelection[fieldKey] = next;
    onSelectionChange(nextSelection);
  };

  const clearAll = () => onSelectionChange({});

  return (
    <div className="flex w-72 flex-col border-l bg-muted/30">
      <div className="border-b px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Metadata filters</span>
          {activeFilterCount > 0 && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 gap-1 px-2 text-xs"
              onClick={clearAll}
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          )}
        </div>
        <div className="text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{matchedCount}</span>{" "}
          of {totalCount} studies match
        </div>
      </div>
      <ScrollArea className="flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block">
        {filterFields.length === 0 ? (
          <div className="p-4 text-xs text-muted-foreground">
            No metadata available on this project's documents yet.
          </div>
        ) : (
          <ul className="py-1">
            {filterFields.map((field) => (
              <FilterSection
                key={field.key}
                field={field}
                selectedValues={selection[field.key]}
                onToggle={(value) => toggleValue(field.key, value)}
              />
            ))}
          </ul>
        )}
      </ScrollArea>
    </div>
  );
};

interface FilterSectionProps {
  field: FilterField;
  selectedValues: Set<string> | undefined;
  onToggle: (value: string) => void;
}

const FilterSection = ({ field, selectedValues, onToggle }: FilterSectionProps) => {
  // Open by default when the section already has a selection so the user can
  // see what's active without an extra click.
  const [open, setOpen] = useState(() => !!selectedValues && selectedValues.size > 0);
  const activeCount = selectedValues?.size ?? 0;

  return (
    <li>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-brand-teal hover:bg-accent/40"
          >
            <span className="truncate">
              {field.label}
              {activeCount > 0 && (
                <span className="ml-1 rounded-full bg-brand-teal/10 px-1.5 py-0.5 text-[10px] font-medium text-brand-teal">
                  {activeCount}
                </span>
              )}
            </span>
            <ChevronDown
              className={cn(
                "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
                open && "rotate-180",
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="pb-1">
            {field.values.map(({ value, count }) => {
              const selected = selectedValues?.has(value) ?? false;
              return (
                <li key={value}>
                  <button
                    type="button"
                    onClick={() => onToggle(value)}
                    title={value}
                    className={cn(
                      "flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-accent/40",
                      selected && "text-brand-teal",
                    )}
                  >
                    <span
                      className={cn(
                        "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                        selected
                          ? "border-brand-teal bg-brand-teal text-white"
                          : "border-input bg-background",
                      )}
                    >
                      {selected && <Check className="h-3 w-3" />}
                    </span>
                    <span className="flex-1 truncate">{value}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {count}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    </li>
  );
};

export default ProjectChatFilters;
